import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-slate-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <h1 className="text-xl font-bold text-[#101E4D]">FLOW</h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <article className="bg-white rounded-xl shadow-sm p-8 space-y-6">
          <div className="border-b pb-6">
            <h1 className="text-3xl font-bold text-[#101E4D] mb-2">
              Conditions Générales d'Utilisation
            </h1>
            <p className="text-slate-500">
              Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">1. Objet</h2>
            <p className="text-slate-600 leading-relaxed">
              Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'utilisation 
              de la plateforme FLOW, un programme de coaching autonome de 90 jours, édité par WEDO Coaching.
            </p>
            <p className="text-slate-600 leading-relaxed">
              En vous inscrivant et en utilisant FLOW, vous acceptez sans réserve les présentes CGU.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">2. Description du service</h2>
            <p className="text-slate-600 leading-relaxed">
              FLOW est un parcours de développement personnel en ligne comprenant :
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
              <li>6 modules principaux débloqués progressivement sur 90 jours</li>
              <li>Des contenus personnalisés selon votre profil de personnalité</li>
              <li>Des vidéos, textes, exercices et rituels d'ancrage</li>
              <li>Un suivi de progression personnel</li>
              <li>Des rappels par email pour maintenir votre engagement</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              L'accès au programme nécessite un rendez-vous de clarté initiale avec un coach certifié 
              pour déterminer votre profil. Ce rendez-vous n'est pas inclus dans le prix du programme FLOW.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">3. Inscription et compte</h2>
            <h3 className="text-lg font-medium text-[#101E4D]">3.1 Création de compte</h3>
            <p className="text-slate-600 leading-relaxed">
              Pour accéder à FLOW, vous devez créer un compte en fournissant des informations exactes et complètes. 
              Vous êtes responsable de la confidentialité de vos identifiants de connexion.
            </p>
            
            <h3 className="text-lg font-medium text-[#101E4D]">3.2 Sécurité du mot de passe</h3>
            <p className="text-slate-600 leading-relaxed">
              Votre mot de passe doit respecter les critères de sécurité suivants :
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-600 ml-4">
              <li>Minimum 8 caractères</li>
              <li>Au moins une lettre majuscule et une lettre minuscule</li>
              <li>Au moins un chiffre</li>
              <li>Au moins un caractère spécial</li>
            </ul>

            <h3 className="text-lg font-medium text-[#101E4D]">3.3 Validation du compte</h3>
            <p className="text-slate-600 leading-relaxed">
              L'accès au contenu est activé par un administrateur après validation de votre inscription 
              et réception du paiement. Un email de bienvenue vous sera envoyé avec un lien pour 
              créer votre mot de passe définitif.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">4. Tarifs et paiement</h2>
            <p className="text-slate-600 leading-relaxed">
              Le tarif du programme FLOW est communiqué lors de votre inscription. Le paiement peut être effectué :
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
              <li>En une fois (paiement intégral)</li>
              <li>En 2 fois (paiement en 2x)</li>
              <li>En 3 fois (paiement en 3x)</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              En cas de retard de paiement pour les paiements échelonnés, l'accès au programme pourra être suspendu 
              jusqu'à régularisation.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">5. Droit de rétractation</h2>
            <p className="text-slate-600 leading-relaxed">
              Conformément à l'article L221-18 du Code de la consommation, vous disposez d'un délai de 14 jours 
              à compter de la validation de votre inscription pour exercer votre droit de rétractation, 
              sans avoir à justifier de motifs ni à payer de pénalités.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Toutefois, si vous avez commencé à utiliser le service (accès à un module), vous acceptez 
              que le remboursement soit calculé au prorata du contenu non consommé.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">6. Propriété intellectuelle</h2>
            <p className="text-slate-600 leading-relaxed">
              L'ensemble du contenu de FLOW (textes, vidéos, exercices, graphiques, structure pédagogique) 
              est protégé par le droit d'auteur et appartient à WEDO Coaching.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Vous bénéficiez d'un droit d'usage personnel et non-cessible pendant la durée de votre accès au programme. 
              Toute reproduction, diffusion, revente ou partage du contenu est strictement interdite.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">7. Responsabilité</h2>
            <p className="text-slate-600 leading-relaxed">
              FLOW est un programme de développement personnel et ne constitue pas une thérapie ni un traitement médical. 
              Le programme ne se substitue pas à un accompagnement médical ou psychologique si nécessaire.
            </p>
            <p className="text-slate-600 leading-relaxed">
              WEDO Coaching ne peut être tenu responsable des résultats obtenus, qui dépendent de l'implication 
              personnelle de chaque participant.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">8. Protection des données</h2>
            <p className="text-slate-600 leading-relaxed">
              Vos données personnelles sont traitées conformément à notre{" "}
              <button
                onClick={() => navigate("/privacy")}
                className="text-[#FF6B35] hover:underline"
              >
                Politique de Confidentialité
              </button>
              , dans le respect du Règlement Général sur la Protection des Données (RGPD).
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">9. Résiliation</h2>
            <p className="text-slate-600 leading-relaxed">
              Vous pouvez demander la suppression de votre compte à tout moment via votre espace personnel 
              ou en nous contactant. La suppression entraîne la perte définitive de l'accès au programme 
              et de vos données de progression.
            </p>
            <p className="text-slate-600 leading-relaxed">
              WEDO Coaching se réserve le droit de suspendre ou résilier l'accès d'un utilisateur en cas de :
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
              <li>Non-respect des présentes CGU</li>
              <li>Défaut de paiement</li>
              <li>Usage frauduleux ou abusif du service</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">10. Modification des CGU</h2>
            <p className="text-slate-600 leading-relaxed">
              WEDO Coaching se réserve le droit de modifier les présentes CGU à tout moment. 
              Les utilisateurs seront informés de toute modification substantielle par email. 
              L'utilisation continue du service après modification vaut acceptation des nouvelles CGU.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">11. Droit applicable et litiges</h2>
            <p className="text-slate-600 leading-relaxed">
              Les présentes CGU sont régies par le droit français. En cas de litige, une solution amiable 
              sera recherchée avant toute action judiciaire. À défaut, les tribunaux français seront compétents.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Conformément à l'article L612-1 du Code de la consommation, vous pouvez recourir gratuitement 
              à un médiateur de la consommation en cas de litige.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#101E4D]">12. Contact</h2>
            <p className="text-slate-600 leading-relaxed">
              Pour toute question relative aux présentes CGU :
            </p>
            <div className="bg-slate-50 p-4 rounded-lg text-slate-600">
              <p><strong>WEDO COACHING & CONSEIL</strong></p>
              <p>35 Cours Pierre Puget, 13006 Marseille</p>
              <p>Email : contact@wedocoaching.com</p>
            </div>
          </section>
        </article>
      </main>
    </div>
  );
};

export default TermsOfService;
